import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Leaf, CheckCircle } from "lucide-react";
import heroImage from "@/assets/hero-packaging.jpg";

const HeroSection = () => {
  return (
    <section 
      className="relative min-h-screen flex items-center overflow-hidden"
      aria-labelledby="hero-heading"
    >
      {/* Bold gradient background */}
      <div className="absolute inset-0 z-0">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-accent/5" />
        <div className="absolute top-0 right-0 w-1/2 h-full">
          <img
            src={heroImage}
            alt="Emballages alimentaires durables AMS DISTRIB - Bols kraft, gobelets et sacs écologiques"
            className="w-full h-full object-cover"
            loading="eager"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-transparent" />
        </div>
        {/* Decorative geometric shapes */}
        <div className="absolute top-20 left-10 w-72 h-72 rounded-full bg-primary/20 blur-[100px]" />
        <div className="absolute bottom-32 left-1/4 w-48 h-48 rounded-full bg-eco/15 blur-[80px]" />
        <div className="absolute top-1/3 right-1/3 w-32 h-32 border-2 border-primary/20 rounded-full animate-pulse" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10 pt-24">
        <div className="max-w-2xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-2 mb-6"
          >
            <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-eco text-eco-foreground rounded-full text-sm font-semibold shadow-lg shadow-eco/25">
              <Leaf className="w-4 h-4" aria-hidden="true" />
              Emballage éco-responsable
            </span>
          </motion.div>

          <motion.h1
            id="hero-heading"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-8"
          >
            <span className="text-foreground">Le meilleur de</span>{" "}
            <span className="text-gradient-blue">l'emballage durable</span>
            <br />
            <span className="text-foreground">sans compromis</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-muted-foreground mb-8 leading-relaxed max-w-xl"
          >
            Votre fournisseur d'emballage alimentaire à Bordeaux, Toulouse et Paris. 
            Solutions certifiées FSC, respectueuses de l'environnement et conformes 
            aux normes européennes les plus strictes.
          </motion.p>

          {/* Benefits list - More prominent */}
          <motion.ul
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.25 }}
            className="flex flex-col gap-3 mb-10"
          >
            {[
              "Produits certifiés FSC et ISO 9001",
              "Livraison rapide Bordeaux, Toulouse, Paris et toute la France", 
              "Personnalisation avec votre logo"
            ].map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-foreground font-medium">
                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-eco text-eco-foreground">
                  <CheckCircle className="w-4 h-4" aria-hidden="true" />
                </span>
                {benefit}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Button asChild variant="hero" size="xl" className="shadow-xl shadow-primary/30 hover:shadow-2xl hover:shadow-primary/40 transition-all">
              <Link to="/produits">
                Découvrir nos produits
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            </Button>
            <Button asChild variant="heroOutline" size="xl" className="border-2 hover:bg-accent hover:text-accent-foreground hover:border-accent transition-all">
              <Link to="/devis">
                Demander un devis gratuit
              </Link>
            </Button>
          </motion.div>

          {/* Stats - More visual impact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="flex flex-wrap gap-10 mt-16 pt-10 border-t-2 border-primary/20"
            role="list"
            aria-label="Nos certifications"
          >
            {[
              { value: "100%", label: "Certifié FSC" },
              { value: "ISO", label: "9001:2015" },
              { value: "UE", label: "Normes alimentaires" },
              { value: "50+", label: "Produits disponibles" }
            ].map((stat, index) => (
              <div key={index} role="listitem" className="text-center">
                <p className="font-display text-4xl md:text-5xl font-bold text-primary drop-shadow-sm">{stat.value}</p>
                <p className="text-sm text-muted-foreground mt-1 font-medium">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Animated decorative elements */}
      <motion.div
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-20 right-20 w-40 h-40 rounded-3xl bg-gradient-to-br from-primary/30 to-eco/20 blur-2xl hidden lg:block"
        aria-hidden="true"
      />
      <motion.div
        animate={{ y: [0, 15, 0], x: [0, -10, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
        className="absolute top-40 right-40 w-24 h-24 rounded-full border-4 border-primary/30 hidden lg:block"
        aria-hidden="true"
      />
    </section>
  );
};

export default HeroSection;